import { useState } from 'react';
import toast from 'react-hot-toast';
import {
  Box,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  CircularProgress,
} from '@mui/material';
import { saveEmailAsDraft } from '../../../utils/graphMailService';
import { T, Icon, stitchInputSx, stitchGradientBtn, stitchOutlineBtn, stitchDialogPaper, StitchDivider, StitchIconBtn } from './stitch';


// Milestones per deliverable uit de ECHTE taakplanning (Joris, 1 sep):
// "Montage v1" → V1 Oplevering = einddatum, Feedback V1 = de klant-deadline op
// die taak; "… DEF" → Definitieve versie. De oude afleiding pakte gewoon de
// eerste vijf datums van het hele project en klopte daardoor niet (9402).
const _kern = (n) => String(n || '').split(' - ')[0].trim().toLowerCase();
const _iso = (v) => (v ? String(v).slice(0, 10) : '');
// Werkdag vóór een datum: feedback vragen we uiterlijk de dag voordat de
// volgende versie start (Joris, 1 sep); in het weekend → de vrijdag ervoor.
const _werkdagVoor = (iso) => {
  if (!iso) return '';
  const d = new Date(`${_iso(iso)}T12:00:00`);
  if (Number.isNaN(d.getTime())) return '';
  d.setDate(d.getDate() - 1);
  while (d.getDay() === 0 || d.getDay() === 6) d.setDate(d.getDate() - 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

// Deliverable-titel opschonen voor de klantmail: technische staarten als
// "n.t.b.", "incl. ondertiteling …" en beeldverhoudingen ("16:9 / 1:1") zijn
// interne administratie — de klant leest alleen de kern (Joris, 1 sep).
const _ruisSegment = (seg) => /^n\.?t\.?b/i.test(seg) || /^incl\./i.test(seg)
  || /\d{1,2}:\d{1,2}/.test(seg) || seg === '' || seg === '.';
export function schoonDeliverableTitel(titel) {
  const segs = String(titel || '').split(/\s+-\s*/);
  const uit = [segs[0]];
  for (const seg of segs.slice(1)) {
    if (_ruisSegment(seg.trim())) break;
    uit.push(seg.trim());
  }
  return uit.join(' - ').replace(/[\s\-.]+$/, '').trim();
}

export function milestonesUitTaken(taken, deliverableDeadline) {
  const uit = [];
  const vind = (pred) => (taken || []).find((t) => pred(_kern(t.naam)));
  const def = vind((k) => k.includes('def'));
  for (const n of [1, 2, 3]) {
    const t = vind((k) => k.includes(`v${n}`) && !k.includes('intern'));
    if (t) {
      const lever = _iso(t.eind || t.start);
      if (lever) uit.push({ label: `V${n} Oplevering`, date: lever });
      // Feedbackmoment: de klant-deadline op de taak als die er is, anders de
      // werkdag vóór de start van de vólgende versie (of de DEF) — dan weet de
      // klant wanneer wij hun feedback nodig hebben.
      let fb = _iso(t.deadline);
      if (!fb) {
        const opvolger = vind((k) => k.includes(`v${n + 1}`) && !k.includes('intern')) || def;
        if (opvolger && opvolger !== t) fb = _werkdagVoor(opvolger.start);
      }
      if (fb && fb >= (lever || fb)) uit.push({ label: `Feedback V${n}`, date: fb });
    }
  }
  const defDatum = _iso(def?.eind || def?.deadline) || _iso(deliverableDeadline);
  if (defDatum) uit.push({ label: 'Definitieve versie', date: defDatum });
  uit.sort((a, b) => a.date.localeCompare(b.date));
  return uit.length ? uit : null;
}

// Groepen (deliverables + hun taken) uit de ProjectOverview-shapes bouwen.
function bouwGroepen(deliverables, tasks) {
  return (deliverables || []).map((d) => ({
    id: d.id,
    titel: d.fields?.['Naam van Deliverable'] || d.fields?.Deliverable || 'Deliverable',
    deadline: d.fields?.Deadline || null,
    taken: (tasks || [])
      .filter((t) => (t.fields?.Deliverable || []).includes?.(d.id))
      .map((t) => ({
        naam: t.fields?.NaamTaak, type: t.fields?.Type,
        start: t.fields?.StartDateUitvoerder, eind: t.fields?.EndDateUitvoerder,
        deadline: t.fields?.Deadline,
      })),
  }));
}

// ===== PLANNING EMAIL DIALOG =====
function PlanningEmailDialog({ open, onClose, project, deliverables, tasks, groepen, defaultGeselecteerd, msalInstance, account, senderName }) {
  const projectName = project?.fields?.Projectnaam || project?.fields?.Projectnr_en_naam || 'Project';
  const clientName = (() => {
    const names = project?.fields?.KlantnaamString || project?.fields?.Klantnaam || [];
    return Array.isArray(names) ? names[0] : names || '';
  })();
  const contactName = (() => {
    const cp = project?.fields?.['Volledige naam Contactpersoon (from Contactpersoon)'] || [];
    return Array.isArray(cp) ? cp[0] : cp || '';
  })();
  const contactEmail = (() => {
    const em = project?.fields?.emailCP || [];
    return Array.isArray(em) ? em[0] : em || '';
  })();

  // Deliverable-groepen: meegegeven (Overzicht per project) of afgeleid uit de
  // project-shapes. Multiselect: de mail kan meerdere deliverables beslaan;
  // gelijk geplande deliverables delen straks één sectie.
  const alleGroepen = (groepen && groepen.length ? groepen : bouwGroepen(deliverables, tasks))
    .filter((g) => g.taken.length || g.deadline);
  const [gekozen, setGekozen] = useState(() => new Set(
    defaultGeselecteerd?.length ? defaultGeselecteerd : alleGroepen.map((g) => g.id)));

  // Secties: per unieke milestone-set één blok, kop = de deliverables die hem delen.
  const maakSecties = (ids) => {
    const perSig = new Map();
    alleGroepen.filter((g) => ids.has(g.id)).forEach((g) => {
      const ms = milestonesUitTaken(g.taken, g.deadline) || deriveMilestones(
        [{ fields: { Deadline: g.deadline } }],
        g.taken.map((t) => ({ fields: { Type: t.type, Deadline: t.deadline, EndDateUitvoerder: t.eind } })));
      const sig = JSON.stringify(ms);
      if (!perSig.has(sig)) perSig.set(sig, { titels: [], milestones: ms.map((m) => ({ ...m })) });
      perSig.get(sig).titels.push(g.titel);
    });
    return [...perSig.values()].map((x) => ({
      kop: x.titels.map(schoonDeliverableTitel).filter(Boolean).join(' + '),
      milestones: x.milestones,
    }));
  };
  const [secties, setSecties] = useState(() => maakSecties(new Set(
    defaultGeselecteerd?.length ? defaultGeselecteerd : alleGroepen.map((g) => g.id))));
  const wisselGroep = (id) => {
    setGekozen((prev) => {
      const n = new Set(prev);
      if (n.has(id)) n.delete(id); else n.add(id);
      setSecties(maakSecties(n));
      return n;
    });
  };

  const [recipientEmail, setRecipientEmail] = useState(contactEmail);
  const [recipientName, setRecipientName] = useState(contactName ? contactName.split(' ')[0] : '');
  const [saving, setSaving] = useState(false);

  const emailBody = generatePlanningEmailSecties(projectName, recipientName, secties, senderName ? senderName.split(' ')[0] : '');
  const emailSubject = `Planning postproductie \u2014 ${projectName}`;
  const totaalMilestones = secties.reduce((s2, x) => s2 + x.milestones.length, 0);

  const updateMilestone = (si, index, field, value) => {
    setSecties((prev) => prev.map((sec, i) => (i === si
      ? { ...sec, milestones: sec.milestones.map((m, j) => (j === index ? { ...m, [field]: value } : m)) }
      : sec)));
  };
  const removeMilestone = (si, index) => {
    setSecties((prev) => prev.map((sec, i) => (i === si
      ? { ...sec, milestones: sec.milestones.filter((_, j) => j !== index) } : sec)));
  };
  const addMilestone = (si) => {
    setSecties((prev) => prev.map((sec, i) => (i === si
      ? { ...sec, milestones: [...sec.milestones, { label: '', date: '' }] } : sec)));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await saveEmailAsDraft(msalInstance, account, {
        subject: emailSubject,
        body: emailBody,
        to: recipientEmail || undefined,
      });
      toast.success('Planning email opgeslagen als concept in Outlook');
      onClose();
    } catch (err) {
      console.error('Failed to save draft:', err);
      toast.error('Kon email niet opslaan: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth PaperProps={{ sx: stitchDialogPaper }}>
      <DialogTitle sx={{ fontFamily: T.font, fontWeight: 700 }}>
        Mail planning naar klant
      </DialogTitle>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '8px !important' }}>
        {/* Recipient */}
        <Box sx={{ display: 'flex', gap: 2 }}>
          <TextField
            label="Naam ontvanger"
            value={recipientName}
            onChange={e => setRecipientName(e.target.value)}
            size="small"
            sx={{ flex: 1, ...stitchInputSx }}
            placeholder="Voornaam"
          />
          <TextField
            label="Email ontvanger"
            value={recipientEmail}
            onChange={e => setRecipientEmail(e.target.value)}
            size="small"
            sx={{ flex: 1, ...stitchInputSx }}
            placeholder="email@klant.nl"
          />
        </Box>

        <StitchDivider />

        {/* Welke deliverables dekt deze mail — gelijk geplande delen één sectie */}
        {alleGroepen.length > 1 && (
          <>
            <Typography variant="subtitle2" sx={{ fontFamily: T.font, fontWeight: 600 }}>Deliverables</Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
              {alleGroepen.map((g) => (
                <Box key={g.id} component="span" onClick={() => wisselGroep(g.id)}
                  sx={{ ...stitchOutlineBtn, fontSize: '0.72rem', px: 1, py: 0.25,
                    opacity: gekozen.has(g.id) ? 1 : 0.45,
                    borderColor: gekozen.has(g.id) ? T.primary : undefined }}>
                  {gekozen.has(g.id) ? '✓ ' : ''}{g.titel}
                </Box>
              ))}
            </Box>
          </>
        )}

        {/* Milestones — per sectie (gelijk geplande deliverables samengevoegd) */}
        <Typography variant="subtitle2" sx={{ fontFamily: T.font, fontWeight: 600 }}>Milestones</Typography>
        {secties.map((sec, si) => (
          <Box key={si} sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {(secties.length > 1 || alleGroepen.length > 1) && (
              <Typography variant="caption" sx={{ fontFamily: T.font, fontWeight: 700, color: T.primary }}>
                {sec.kop}
              </Typography>
            )}
            {sec.milestones.map((m, i) => (
              <Box key={i} sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                <TextField
                  value={m.label}
                  onChange={e => updateMilestone(si, i, 'label', e.target.value)}
                  size="small"
                  sx={{ flex: 2, ...stitchInputSx }}
                  placeholder="Bijv. V1 Oplevering"
                />
                <TextField
                  type="date"
                  value={m.date}
                  onChange={e => updateMilestone(si, i, 'date', e.target.value)}
                  size="small"
                  sx={{ flex: 1, ...stitchInputSx }}
                  InputLabelProps={{ shrink: true }}
                />
                <StitchIconBtn onClick={() => removeMilestone(si, i)} sx={{ color: T.error }}>
                  <Icon name="delete_outline" size={18} />
                </StitchIconBtn>
              </Box>
            ))}
            <Box
              component="span"
              onClick={() => addMilestone(si)}
              sx={{ ...stitchOutlineBtn, alignSelf: 'flex-start', fontSize: '0.75rem' }}
            >
              <Icon name="add" size={16} />
              Milestone toevoegen
            </Box>
          </Box>
        ))}

        <StitchDivider />

        {/* Preview */}
        <Typography variant="subtitle2" sx={{ fontFamily: T.font, fontWeight: 600 }}>Preview</Typography>
        <Box sx={{ p: 2, backgroundColor: T.surfaceLow, borderRadius: '0.75rem' }}>
          <Typography variant="body2" sx={{ fontFamily: T.fontBody, fontWeight: 600, mb: 1 }}>
            {emailSubject}
          </Typography>
          <Typography variant="body2" sx={{ fontFamily: T.fontBody, whiteSpace: 'pre-line', fontSize: '0.85rem', lineHeight: 1.6 }}>
            {emailBody}
          </Typography>
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Box component="span" onClick={onClose} sx={stitchOutlineBtn}>Annuleren</Box>
        <Box
          component="span"
          onClick={(saving || totaalMilestones === 0) ? undefined : handleSave}
          sx={{
            ...stitchGradientBtn,
            opacity: (saving || totaalMilestones === 0) ? 0.5 : 1,
            cursor: (saving || totaalMilestones === 0) ? 'default' : 'pointer',
          }}
        >
          {saving ? <CircularProgress size={18} sx={{ color: '#fff' }} /> : <Icon name="mail" size={18} />}
          {saving ? 'Opslaan...' : 'Opslaan als concept'}
        </Box>
      </DialogActions>
    </Dialog>
  );
}


// Derive milestone dates from deliverables and tasks
function deriveMilestones(deliverables, tasks) {
  const defaultMilestones = [
    { label: 'V1 Oplevering', date: '' },
    { label: 'Feedback V1', date: '' },
    { label: 'V2 Oplevering', date: '' },
    { label: 'Feedback V2', date: '' },
    { label: 'Definitieve versie', date: '' },
  ];

  // Collect deadline dates from deliverables and postproduction tasks
  const postTypes = ['Montage', 'Animatie', 'Grading', 'Audio', 'Regie', 'Illustratie', 'Deadline'];
  const dates = [];

  // Deliverable deadlines (primary source)
  if (deliverables?.length) {
    deliverables.forEach(d => {
      const deadline = d.fields?.Deadline;
      if (deadline) dates.push(new Date(deadline));
    });
  }

  // Task deadlines (secondary source, postproduction only)
  if (tasks?.length) {
    tasks.forEach(t => {
      const type = t.fields?.Type || '';
      if (!postTypes.includes(type)) return;
      const deadline = t.fields?.Deadline || t.fields?.EndDateUitvoerder;
      if (deadline) dates.push(new Date(deadline));
    });
  }

  if (dates.length === 0) return defaultMilestones;

  // Sort dates and pick earliest as V1 basis
  dates.sort((a, b) => a - b);
  const uniqueDates = [...new Set(dates.map(d => d.toISOString().slice(0, 10)))].sort();

  // Try to map dates intelligently
  const addWorkdays = (dateStr, days) => {
    const d = new Date(dateStr);
    let added = 0;
    while (added < days) {
      d.setDate(d.getDate() + 1);
      const day = d.getDay();
      if (day !== 0 && day !== 6) added++;
    }
    return d.toISOString().slice(0, 10);
  };

  // Use first date as V1, derive rest
  const v1 = uniqueDates[0];
  const fb1 = uniqueDates[1] || addWorkdays(v1, 3);
  const v2 = uniqueDates[2] || addWorkdays(fb1, 5);
  const fb2 = uniqueDates[3] || addWorkdays(v2, 3);
  const def = uniqueDates[4] || addWorkdays(fb2, 3);

  return [
    { label: 'V1 Oplevering', date: v1 },
    { label: 'Feedback V1', date: fb1 },
    { label: 'V2 Oplevering', date: v2 },
    { label: 'Feedback V2', date: fb2 },
    { label: 'Definitieve versie', date: def },
  ];
}


// Generate email text in Full Frame tone of voice
function generatePlanningEmail(projectName, recipientName, milestones, senderFirstName) {
  const greeting = recipientName ? `Hi ${recipientName},` : 'Hi,';
  const validMilestones = milestones.filter(m => m.label && m.date);

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    return d.toLocaleDateString('nl-NL', { weekday: 'long', day: 'numeric', month: 'long' });
  };

  const bulletPoints = validMilestones.map(m => {
    const isUiterlijk = m.label.toLowerCase().includes('feedback');
    return `  \u2022 ${m.label}: ${isUiterlijk ? 'uiterlijk ' : ''}${formatDate(m.date)}`;
  }).join('\n');

  const closing = senderFirstName ? `Groet,\n${senderFirstName}` : 'Groet';

  return `${greeting}

Hierbij de planning voor de postproductie van ${projectName}:

${bulletPoints}

Mocht je vragen hebben of lukt een datum niet, laat het gerust weten. Dan kijken we samen naar een oplossing.

${closing}`;
}

// Meerdere secties (deliverable-groepen) in één mail; bij één sectie zonder kopjes.
function generatePlanningEmailSecties(projectName, recipientName, secties, senderFirstName) {
  const bruikbaar = (secties || []).filter((s2) => s2.milestones.some((m) => m.label && m.date));
  if (bruikbaar.length <= 1) {
    return generatePlanningEmail(projectName, recipientName, bruikbaar[0]?.milestones || [], senderFirstName);
  }
  const greeting = recipientName ? `Hi ${recipientName},` : 'Hi,';
  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleDateString('nl-NL', { weekday: 'long', day: 'numeric', month: 'long' });
  };
  const blokken = bruikbaar.map((sec) => {
    const bullets = sec.milestones.filter((m) => m.label && m.date).map((m) => {
      const isUiterlijk = m.label.toLowerCase().includes('feedback');
      return `  \u2022 ${m.label}: ${isUiterlijk ? 'uiterlijk ' : ''}${formatDate(m.date)}`;
    }).join('\n');
    return `${sec.kop}\n${bullets}`;
  }).join('\n\n');
  const closing = senderFirstName ? `Groet,\n${senderFirstName}` : 'Groet';
  return `${greeting}\n\nHierbij de planning voor de postproductie van ${projectName}:\n\n${blokken}\n\nMocht je vragen hebben of lukt een datum niet, laat het gerust weten. Dan kijken we samen naar een oplossing.\n\n${closing}`;
}

export default PlanningEmailDialog;
